import React, { useMemo, useState } from "react";

const ENTITY_OPTIONS = ["VTPL", "RNS", "HUF", "TRS"];
const inrFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");

function apiUrl(path) {
  if (!API_BASE_URL) return path;
  return `${API_BASE_URL}${path}`;
}

const initialForm = {
  tenant_name: "",
  property_name: "",
  entity_name: "VTPL",
  advance_paid: "",
  advance_paid_date: "",
  exempted_amount: "",
  taxable_amount: "",
  received: "",
  received_fy: "",
  received_date: "",
  dues_month: "",
  remarks: ""
};

function parseAmountInput(value) {
  if (value === null || value === undefined || value === "") return NaN;
  const text = String(value)
    .trim()
    .replace(/[₹,\s]/g, "")
    .replace(/[^\d.-]/g, "");
  if (!text || text === "-" || text === ".") return NaN;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : NaN;
}

function amountValueOrZero(value) {
  const parsed = parseAmountInput(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function rentAmountsFromForm(formData) {
  const exempted = parseAmountInput(formData.exempted_amount);
  const taxable = parseAmountInput(formData.taxable_amount);
  return {
    exempted_amount: !Number.isNaN(exempted) && exempted > 0 ? exempted : undefined,
    taxable_amount: !Number.isNaN(taxable) && taxable > 0 ? taxable : undefined
  };
}

function computeRentFields(exemptedAmount, taxableAmount) {
  const exemptedText = String(exemptedAmount ?? "").trim();
  const taxableText = String(taxableAmount ?? "").trim();

  if (exemptedText) {
    const exempted = parseAmountInput(exemptedText);
    if (!Number.isNaN(exempted) && exempted > 0) {
      return {
        rent_type: "exempted",
        exempted_amount: exempted,
        taxable_amount: null,
        gst: null,
        total_rent: exempted,
        tds: null,
        monthly_receivable: exempted
      };
    }
  }

  if (taxableText) {
    const taxable = parseAmountInput(taxableText);
    if (!Number.isNaN(taxable) && taxable > 0) {
      const gst = Math.round(taxable * 0.18 * 100) / 100;
      const total_rent = Math.round((taxable + gst) * 100) / 100;
      const tds = Math.round(taxable * 0.10 * 100) / 100;
      const monthly_receivable = Math.round((total_rent - tds) * 100) / 100;
      return {
        rent_type: "taxable",
        exempted_amount: null,
        taxable_amount: taxable,
        gst,
        total_rent,
        tds,
        monthly_receivable
      };
    }
  }

  return null;
}

function formatRentCell(value, inrFormatter) {
  if (value === null || value === undefined || value === "") return "NA";
  return inrFormatter.format(Number(value || 0));
}

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function buildDueMonthOptions() {
  const options = [];
  const now = new Date();
  for (let offset = -24; offset <= 6; offset += 1) {
    const date = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    options.push(`${MONTH_SHORT[date.getMonth()]}-${date.getFullYear()}`);
  }
  return options;
}

const PASS_SHEET_DUE_MONTH_OPTIONS = buildDueMonthOptions();

function getDueMonthPickerOptions(proposedMonth) {
  const recent = PASS_SHEET_DUE_MONTH_OPTIONS.slice(-15);
  const suggested = parseDueMonthSelection(proposedMonth);
  return [...new Set([...suggested, ...recent])];
}

function getFinancialYearMonthCount() {
  const now = new Date();
  const fyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const fyStart = fyStartYear * 12 + 3;
  const current = now.getFullYear() * 12 + now.getMonth();
  return current - fyStart + 1;
}

function getFinancialYearRangeLabel() {
  const now = new Date();
  const startYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return `Apr-${startYear} to ${MONTH_SHORT[now.getMonth()]}-${now.getFullYear()}`;
}

function getTenantMonthlyReceivable(tenant) {
  const monthly = Number(tenant.monthly_receivable || 0);
  if (monthly > 0) return monthly;
  const computed = computeRentFields(tenant.exempted_amount, tenant.taxable_amount);
  return computed?.monthly_receivable || 0;
}

function buildFyTenantTotals(tenant, fyMonthCount) {
  const monthly = getTenantMonthlyReceivable(tenant);
  const fyReceivable = Number(tenant.fy_receivable) || Math.round(monthly * fyMonthCount * 100) / 100;
  const receivedFy = Number(tenant.received_fy ?? tenant.received ?? 0);
  const balance = Math.round((fyReceivable - receivedFy) * 100) / 100;
  return {
    tenantId: tenant.id,
    tenantName: tenant.tenant_name,
    propertyName: tenant.property_name,
    monthlyReceivable: monthly,
    fyReceivable,
    received: receivedFy,
    balance,
    duesMonth: tenant.dues_month || "-"
  };
}

const MONTH_INDEX = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  sept: 8,
  oct: 9,
  nov: 10,
  dec: 11
};

function parseDueMonthEntries(value) {
  if (!value) return [];
  const text = String(value).toLowerCase();
  const matches = [...text.matchAll(/([a-z]{3,9})(?:\s*&\s*([a-z]{3,9}))?\s*[-/ ]\s*(\d{4})/g)];
  const entries = [];

  matches.forEach((match) => {
    const year = Number(match[3]);
    const firstMonth = MONTH_INDEX[match[1]?.slice(0, 4).replace(".", "")];
    const secondMonth = match[2] ? MONTH_INDEX[match[2]?.slice(0, 4).replace(".", "")] : undefined;

    if (firstMonth !== undefined) {
      entries.push({ month: firstMonth, year });
    }
    if (secondMonth !== undefined) {
      entries.push({ month: secondMonth, year });
    }
  });

  return entries;
}

function parseDueMonthSelection(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  const entries = parseDueMonthEntries(value);
  if (entries.length > 0) {
    return entries.map((entry) => `${MONTH_SHORT[entry.month]}-${entry.year}`);
  }
  const single = String(value).trim().match(/^([A-Za-z]{3})-(\d{4})$/);
  return single ? [single[0]] : [];
}

function formatDueMonthSelection(months) {
  const unique = [...new Set((months || []).filter(Boolean))];
  if (unique.length === 0) return "";
  if (unique.length === 1) return unique[0];

  const parsed = unique
    .map((month) => {
      const match = month.match(/^([A-Za-z]{3})-(\d{4})$/);
      return match ? { label: match[1], year: match[2] } : null;
    })
    .filter(Boolean);

  const years = [...new Set(parsed.map((entry) => entry.year))];
  if (years.length === 1) {
    return `${parsed.map((entry) => entry.label).join(" & ")}-${years[0]}`;
  }
  return unique.join(" & ");
}

function getDueClass(value) {
  const parsedEntries = parseDueMonthEntries(value);

  if (parsedEntries.length > 0) {
    const now = new Date();
    const currentMonthNumber = now.getFullYear() * 12 + now.getMonth();
    const isBeforeCurrentMonth = parsedEntries.some(
      (entry) => entry.year * 12 + entry.month < currentMonthNumber
    );
    if (isBeforeCurrentMonth) return "due-overdue";
    return "";
  }

  return "";
}

function formatDisplayDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

function toGoogleMapsUrl(value) {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(trimmed)}`;
}

function GoogleMapLink({ value }) {
  const url = toGoogleMapsUrl(value);
  if (!url) return <span>-</span>;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="map-link">
      Open in Google Maps
    </a>
  );
}

function formatDisplayDateList(value) {
  if (!value) return "-";
  const parts = String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

  if (parts.length === 0) return "-";

  return parts
    .map((item) => {
      const formatted = formatDisplayDate(item);
      return formatted === "-" ? item : formatted;
    })
    .join(", ");
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeEntity, setActiveEntity] = useState("VTPL");
  const [activeSection, setActiveSection] = useState("financials");
  const [activeForm, setActiveForm] = useState("");
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [propertyDetailPropertyId, setPropertyDetailPropertyId] = useState("");
  const [propertyDetailForm, setPropertyDetailForm] = useState({
    property_name: "",
    physical_address: "",
    google_map_location: "",
    building_type: "",
    sq_ft: "",
    other_comments: ""
  });
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [tenants, setTenants] = useState([]);
  const [properties, setProperties] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [newPropertyName, setNewPropertyName] = useState("");
  const [formData, setFormData] = useState(initialForm);
  const [editingTenantId, setEditingTenantId] = useState(null);
  const [generalMessage, setGeneralMessage] = useState("");
  const [uploadInfo, setUploadInfo] = useState({
    tenant_id: "",
    property_name: "",
    document_name: "",
    file: null
  });
  const [tenantDetailForm, setTenantDetailForm] = useState({
    tenant_id: "",
    tenant_email: "",
    tenant_contact_no: "",
    tenant_address: ""
  });
  const [openAiKeyInput, setOpenAiKeyInput] = useState("");
  const [openAiConfigured, setOpenAiConfigured] = useState(false);
  const [passSheetFile, setPassSheetFile] = useState(null);
  const [passSheetScanning, setPassSheetScanning] = useState(false);
  const [passSheetResult, setPassSheetResult] = useState(null);
  const [passSheetApprovals, setPassSheetApprovals] = useState({});
  const [passSheetDueMonths, setPassSheetDueMonths] = useState({});

  const entityProperties = useMemo(
    () => properties.filter((item) => item.entity_name === activeEntity),
    [properties, activeEntity]
  );

  const groupedPropertyData = useMemo(
    () =>
      entityProperties.map((property) => ({
        ...property,
        tenants: tenants.filter((tenant) => tenant.property_name === property.property_name)
      })),
    [entityProperties, tenants]
  );

  const fyMonthCount = useMemo(() => getFinancialYearMonthCount(), []);
  const fyRangeLabel = useMemo(() => getFinancialYearRangeLabel(), []);

  const fyTotalsByProperty = useMemo(() => {
    return groupedPropertyData.map((property) => {
      const tenantRows = property.tenants.map((tenant) => buildFyTenantTotals(tenant, fyMonthCount));
      const propertyTotals = tenantRows.reduce(
        (acc, row) => ({
          fyReceivable: acc.fyReceivable + row.fyReceivable,
          received: acc.received + row.received,
          balance: acc.balance + row.balance
        }),
        { fyReceivable: 0, received: 0, balance: 0 }
      );
      return {
        propertyId: property.id,
        propertyName: property.property_name,
        tenants: tenantRows,
        totals: propertyTotals
      };
    });
  }, [groupedPropertyData, fyMonthCount]);

  const entityTotals = useMemo(() => {
    const totals = fyTotalsByProperty.reduce(
      (acc, property) => ({
        totalReceivable: acc.totalReceivable + property.totals.fyReceivable,
        totalReceived: acc.totalReceived + property.totals.received,
        totalBalance: acc.totalBalance + property.totals.balance
      }),
      { totalReceivable: 0, totalReceived: 0, totalBalance: 0 }
    );
    return totals;
  }, [fyTotalsByProperty]);

  const documentsByProperty = useMemo(() => {
    const grouped = {};
    documents.forEach((doc) => {
      const key = doc.property_name || "Unassigned";
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(doc);
    });
    return grouped;
  }, [documents]);

  const loadProperties = async () => {
    const response = await fetch(apiUrl("/properties"));
    const data = await response.json();
    setProperties(data);
  };

  const loadTenants = async (entity = activeEntity) => {
    const response = await fetch(apiUrl(`/tenants?entity_name=${encodeURIComponent(entity)}`));
    const data = await response.json();
    setTenants(data);
  };

  const loadDocuments = async (entity = activeEntity) => {
    const response = await fetch(apiUrl(`/documents?entity_name=${encodeURIComponent(entity)}`));
    const data = await response.json();
    setDocuments(data);
  };

  const loadOpenAiKeyStatus = async () => {
    const response = await fetch(apiUrl("/settings/openai-api-key"));
    const data = await response.json();
    setOpenAiConfigured(Boolean(data.configured));
  };

  const saveOpenAiKey = async (event) => {
    event.preventDefault();
    if (!openAiKeyInput.trim()) {
      setGeneralMessage("Enter an OpenAI API key");
      return;
    }
    const response = await fetch(apiUrl("/settings/openai-api-key"), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ api_key: openAiKeyInput.trim() })
    });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Unable to save API key");
      return;
    }
    setOpenAiConfigured(true);
    setOpenAiKeyInput("");
    setGeneralMessage("OpenAI API key saved");
  };

  const handlePassSheetFile = (event) => {
    const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
    setPassSheetFile(file);
    setPassSheetResult(null);
    setPassSheetApprovals({});
    setPassSheetDueMonths({});
  };

  const handlePassSheetScan = async (event) => {
    event.preventDefault();
    if (!passSheetFile) {
      setGeneralMessage("Select an Excel pass sheet file first");
      return;
    }
    setPassSheetScanning(true);
    setGeneralMessage("");
    const body = new FormData();
    body.append("file", passSheetFile);
    body.append("entity_name", activeEntity);
    body.append("use_ai", "false");
    try {
      const response = await fetch(apiUrl("/pass-sheet/scan"), { method: "POST", body });
      const result = await response.json();
      if (!response.ok) {
        setGeneralMessage(result.message || "Pass sheet scan failed");
        return;
      }
      setPassSheetResult(result);
      const initialApprovals = {};
      const initialDueMonths = {};
      (result.matches || []).forEach((row) => {
        initialDueMonths[row.match_id] = parseDueMonthSelection(row.proposed_dues_month);
        if (row.tenant_id && row.tenant_found) {
          initialApprovals[row.match_id] = true;
        }
      });
      setPassSheetApprovals(initialApprovals);
      setPassSheetDueMonths(initialDueMonths);
      setGeneralMessage(
        `Found ${result.rent_payments_found || 0} expected tenant payment rule(s) in pass sheet`
      );
    } catch {
      setGeneralMessage("Pass sheet scan failed — check backend is running");
    } finally {
      setPassSheetScanning(false);
    }
  };

  const togglePassSheetApproval = (matchId) => {
    setPassSheetApprovals((prev) => ({ ...prev, [matchId]: !prev[matchId] }));
  };

  const togglePassSheetDueMonth = (matchId, month, checked) => {
    setPassSheetDueMonths((prev) => {
      const current = prev[matchId] || [];
      const next = checked ? [...new Set([...current, month])] : current.filter((item) => item !== month);
      return { ...prev, [matchId]: next };
    });
  };

  const handlePassSheetApply = async () => {
    if (!passSheetResult) return;
    const approvals = (passSheetResult.matches || [])
      .filter((row) => passSheetApprovals[row.match_id] && row.tenant_id)
      .map((row) => ({
        tenant_id: row.tenant_id,
        deposit_amount: row.deposit_amount,
        dues_month: formatDueMonthSelection(
          passSheetDueMonths[row.match_id]?.length
            ? passSheetDueMonths[row.match_id]
            : parseDueMonthSelection(row.proposed_dues_month)
        ),
        approved: true
      }));
    const unpaidTenants = (passSheetResult.missing_tenants || [])
      .filter((item) => item.tenant_id)
      .map((item) => ({ tenant_id: item.tenant_id }));

    if (approvals.length === 0 && unpaidTenants.length === 0) {
      setGeneralMessage("Select at least one payment to approve, or ensure missing tenants exist in the database");
      return;
    }
    if (approvals.some((item) => !item.dues_month)) {
      setGeneralMessage("Select at least one due month for each approved payment");
      return;
    }
    const confirmParts = [];
    if (approvals.length > 0) confirmParts.push(`${approvals.length} rent payment(s)`);
    if (unpaidTenants.length > 0) confirmParts.push(`${unpaidTenants.length} property(ies) marked as rent not received`);
    if (!window.confirm(`Apply ${confirmParts.join(" and ")}?`)) return;

    const response = await fetch(apiUrl("/pass-sheet/apply"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        entity_name: activeEntity,
        approvals,
        unpaid_tenants: unpaidTenants
      })
    });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Unable to apply updates");
      return;
    }
    setGeneralMessage(result.message || "Pass sheet updates applied");
    setPassSheetResult(null);
    setPassSheetApprovals({});
    setPassSheetDueMonths({});
    setPassSheetFile(null);
    const fileInput = document.getElementById("pass-sheet-file");
    if (fileInput) fileInput.value = "";
    await loadTenants();
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginError("");
    const response = await fetch(apiUrl("/login"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(loginForm)
    });
    const result = await response.json();
    if (!response.ok) {
      setLoginError(result.message || "Login failed");
      return;
    }
    setIsLoggedIn(true);
    await loadProperties();
    await loadTenants("VTPL");
    await loadDocuments("VTPL");
    await loadOpenAiKeyStatus();
  };

  const resetTenantForm = () => {
    setFormData({ ...initialForm, entity_name: activeEntity, property_name: "" });
    setEditingTenantId(null);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "exempted_amount" && value) {
        next.taxable_amount = "";
      }
      if (name === "taxable_amount" && value) {
        next.exempted_amount = "";
      }
      return next;
    });
  };

  const formRentPreview = useMemo(
    () => computeRentFields(formData.exempted_amount, formData.taxable_amount),
    [formData.exempted_amount, formData.taxable_amount]
  );

  const editingTenant = useMemo(
    () => (editingTenantId ? tenants.find((item) => item.id === editingTenantId) : null),
    [editingTenantId, tenants]
  );

  const editFormBalance = useMemo(() => {
    const receivable = Number(editingTenant?.receivable || formRentPreview?.monthly_receivable || 0);
    const received = amountValueOrZero(formData.received);
    return receivable - received;
  }, [editingTenant, formData.received, formRentPreview]);

  const editFormFyBalance = useMemo(() => {
    const fyReceivable = Number(
      editingTenant?.fy_receivable ||
        (formRentPreview?.monthly_receivable || getTenantMonthlyReceivable(editingTenant || {})) *
          fyMonthCount
    );
    const receivedFy = amountValueOrZero(formData.received_fy);
    return fyReceivable - receivedFy;
  }, [editingTenant, formData.received_fy, formRentPreview, fyMonthCount]);

  const handleTenantSubmit = async (event) => {
    event.preventDefault();
    setGeneralMessage("");
    const method = editingTenantId ? "PUT" : "POST";
    const url = editingTenantId ? `/tenants/${editingTenantId}` : "/tenants";
    const existingTenant = editingTenantId
      ? tenants.find((item) => item.id === editingTenantId)
      : null;

    const rentAmounts = rentAmountsFromForm(formData);

    if (!editingTenantId && !rentAmounts.exempted_amount && !rentAmounts.taxable_amount) {
      setGeneralMessage("Enter a valid Exempted or Taxable monthly rent amount");
      return;
    }

    const basePayload = {
      ...rentAmounts,
      received: amountValueOrZero(formData.received),
      received_fy: amountValueOrZero(formData.received_fy),
      received_date: formData.received_date || null,
      dues_month: formData.dues_month,
      remarks: formData.remarks
    };

    const payload = editingTenantId && existingTenant
      ? {
          tenant_name: existingTenant.tenant_name,
          property_name: existingTenant.property_name,
          entity_name: activeEntity,
          advance_paid: existingTenant.advance_paid,
          advance_paid_date: existingTenant.advance_paid_date,
          tenant_email: existingTenant.tenant_email,
          tenant_contact_no: existingTenant.tenant_contact_no,
          tenant_address: existingTenant.tenant_address,
          ...basePayload
        }
      : {
          ...formData,
          entity_name: activeEntity,
          advance_paid: amountValueOrZero(formData.advance_paid),
          ...basePayload
        };
    const response = await fetch(apiUrl(url), {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Unable to save tenant");
      return;
    }
    setGeneralMessage(editingTenantId ? "Tenant updated" : "Tenant added");
    resetTenantForm();
    await loadTenants();
  };

  const startEditTenant = (tenant) => {
    setEditingTenantId(tenant.id);
    setFormData({
      tenant_name: tenant.tenant_name || "",
      property_name: tenant.property_name || "",
      entity_name: tenant.entity_name || activeEntity,
      advance_paid: tenant.advance_paid || "",
      advance_paid_date: tenant.advance_paid_date || "",
      exempted_amount: tenant.exempted_amount ?? "",
      taxable_amount: tenant.taxable_amount ?? "",
      received: tenant.received ?? "",
      received_fy: tenant.received_fy ?? tenant.received ?? "",
      received_date: tenant.received_date || "",
      dues_month: tenant.dues_month || "",
      remarks: tenant.remarks || ""
    });
    setActiveForm("add-tenant");
  };

  const renderRentPreviewFields = () => (
    <>
      <div className="computed-field">
        GST: {formRentPreview ? formatRentCell(formRentPreview.gst, inrFormatter) : "NA"}
      </div>
      <div className="computed-field">
        Total Rent: {formRentPreview ? formatRentCell(formRentPreview.total_rent, inrFormatter) : "-"}
      </div>
      <div className="computed-field">
        TDS: {formRentPreview ? formatRentCell(formRentPreview.tds, inrFormatter) : "NA"}
      </div>
      <div className="computed-field">
        Monthly Receivable:{" "}
        {formRentPreview ? formatRentCell(formRentPreview.monthly_receivable, inrFormatter) : "-"}
      </div>
    </>
  );

  const handleDeleteTenant = async (tenantId) => {
    if (!window.confirm("Delete this tenant?")) return;
    await fetch(apiUrl(`/tenants/${tenantId}`), { method: "DELETE" });
    setGeneralMessage("Tenant deleted");
    await loadTenants();
  };

  const handleAddProperty = async (event) => {
    event.preventDefault();
    if (!newPropertyName.trim()) {
      setGeneralMessage("Enter property name");
      return;
    }
    const response = await fetch(apiUrl("/properties"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ entity_name: activeEntity, property_name: newPropertyName.trim() })
    });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Unable to add property");
      return;
    }
    setGeneralMessage("Property added");
    setNewPropertyName("");
    await loadProperties();
    setActiveSection("property-details");
  };

  const handleUploadInput = (event) => {
    const { name, value, files } = event.target;
    if (name === "file") {
      setUploadInfo((prev) => ({ ...prev, file: files && files[0] ? files[0] : null }));
      return;
    }
    setUploadInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpload = async (event) => {
    event.preventDefault();
    if (!uploadInfo.file || !uploadInfo.property_name || !uploadInfo.document_name.trim()) {
      setGeneralMessage("Select property, document name and file first");
      return;
    }
    const body = new FormData();
    body.append("file", uploadInfo.file);
    body.append("tenant_id", uploadInfo.tenant_id);
    body.append("entity_name", activeEntity);
    body.append("property_name", uploadInfo.property_name);
    body.append("document_name", uploadInfo.document_name.trim());
    const response = await fetch(apiUrl("/upload"), { method: "POST", body });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Upload failed");
      return;
    }
    setGeneralMessage(`Uploaded: ${result.file_name}`);
    setUploadInfo({ tenant_id: "", property_name: "", document_name: "", file: null });
    await loadDocuments();
    const fileInput = document.getElementById("doc-file");
    if (fileInput) fileInput.value = "";
  };

  const cancelEditProperty = () => {
    setPropertyDetailPropertyId("");
    setPropertyDetailForm({
      property_name: "",
      physical_address: "",
      google_map_location: "",
      building_type: "",
      sq_ft: "",
      other_comments: ""
    });
  };

  const handleEntityChange = async (entity) => {
    setActiveEntity(entity);
    setSelectedTenantId(null);
    cancelEditProperty();
    setEditingTenantId(null);
    setActiveForm("");
    setActiveSection("financials");
    setPassSheetResult(null);
    setPassSheetApprovals({});
    setPassSheetDueMonths({});
    setFormData((prev) => ({ ...prev, entity_name: entity, property_name: "" }));
    setUploadInfo({ tenant_id: "", property_name: "", document_name: "", file: null });
    setTenantDetailForm({
      tenant_id: "",
      tenant_email: "",
      tenant_contact_no: "",
      tenant_address: ""
    });
    await loadTenants(entity);
    await loadDocuments(entity);
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setActiveSection("financials");
    setActiveForm("");
    setLoginForm({ email: "", password: "" });
    setLoginError("");
    setGeneralMessage("");
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm("Delete this document?")) return;

    const response = await fetch(apiUrl(`/documents/${documentId}`), { method: "DELETE" });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Unable to delete document");
      return;
    }

    setGeneralMessage("Document deleted");
    await loadDocuments();
  };

  const handleDownloadDocument = async (doc) => {
    const response = await fetch(apiUrl(`/documents/${doc.id}/download`));
    if (!response.ok) {
      setGeneralMessage("Unable to download document");
      return;
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = downloadUrl;
    anchor.download = doc.file_name || "document";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(downloadUrl);
  };

  const handleTenantDetailSelection = (event) => {
    const tenantId = event.target.value;
    if (!tenantId) {
      setTenantDetailForm({
        tenant_id: "",
        tenant_email: "",
        tenant_contact_no: "",
        tenant_address: ""
      });
      return;
    }
    const selectedTenant = tenants.find((item) => String(item.id) === String(tenantId));
    if (!selectedTenant) return;
    setTenantDetailForm({
      tenant_id: String(selectedTenant.id),
      tenant_email: selectedTenant.tenant_email || "",
      tenant_contact_no: selectedTenant.tenant_contact_no || "",
      tenant_address: selectedTenant.tenant_address || ""
    });
  };

  const handleTenantDetailInput = (event) => {
    const { name, value } = event.target;
    setTenantDetailForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveTenantDetails = async (event) => {
    event.preventDefault();
    if (!tenantDetailForm.tenant_id) {
      setGeneralMessage("Select a tenant first");
      return;
    }
    const selectedTenant = tenants.find(
      (item) => String(item.id) === String(tenantDetailForm.tenant_id)
    );
    if (!selectedTenant) {
      setGeneralMessage("Tenant not found");
      return;
    }
    const response = await fetch(apiUrl(`/tenants/${selectedTenant.id}`), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        tenant_name: selectedTenant.tenant_name,
        property_name: selectedTenant.property_name,
        entity_name: selectedTenant.entity_name,
        advance_paid: selectedTenant.advance_paid,
        advance_paid_date: selectedTenant.advance_paid_date,
        receivable: selectedTenant.receivable,
        received: selectedTenant.received,
        dues_month: selectedTenant.dues_month,
        remarks: selectedTenant.remarks,
        tenant_email: tenantDetailForm.tenant_email,
        tenant_contact_no: tenantDetailForm.tenant_contact_no,
        tenant_address: tenantDetailForm.tenant_address
      })
    });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Unable to save tenant details");
      return;
    }
    setGeneralMessage("Tenant details updated");
    await loadTenants();
  };

  const formatInr = (value) => inrFormatter.format(Number(value || 0));

  const startEditProperty = (property) => {
    setPropertyDetailPropertyId(String(property.id));
    setPropertyDetailForm({
      property_name: property.property_name || "",
      physical_address: property.physical_address || "",
      google_map_location: property.google_map_location || "",
      building_type: property.building_type || "",
      sq_ft: property.sq_ft || "",
      other_comments: property.other_comments || ""
    });
  };

  const handlePropertyDetailChange = (event) => {
    const { name, value } = event.target;
    setPropertyDetailForm((prev) => ({ ...prev, [name]: value }));
  };

  const savePropertyDetails = async () => {
    if (!propertyDetailPropertyId) {
      setGeneralMessage("Select a property first");
      return;
    }
    const response = await fetch(apiUrl(`/properties/${propertyDetailPropertyId}`), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(propertyDetailForm)
    });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Unable to save property details");
      return;
    }
    setGeneralMessage("Property details updated");
    setPropertyDetailPropertyId("");
    await loadProperties();
    await loadTenants();
    await loadDocuments();
  };

  const handleDeleteProperty = async (propertyId) => {
    if (!window.confirm("Delete this property? This will also delete its tenants and documents.")) return;
    const response = await fetch(apiUrl(`/properties/${propertyId}`), { method: "DELETE" });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Unable to delete property");
      return;
    }
    setGeneralMessage("Property deleted");
    if (String(propertyDetailPropertyId) === String(propertyId)) cancelEditProperty();
    await loadProperties();
    await loadTenants();
    await loadDocuments();
  };

  if (!isLoggedIn) {
    return (
      <div className="login-wrapper">
        <form className="card login-card" onSubmit={handleLogin}>
          <h2>Rent Tracker Login</h2>
          <label>Email</label>
          <input
            type="email"
            value={loginForm.email}
            onChange={(e) => setLoginForm((prev) => ({ ...prev, email: e.target.value }))}
            required
          />
          <label>Password</label>
          <input
            type="password"
            value={loginForm.password}
            onChange={(e) => setLoginForm((prev) => ({ ...prev, password: e.target.value }))}
            required
          />
          {loginError && <p className="error">{loginError}</p>}
          <button type="submit">Login</button>
        </form>
      </div>
    );
  }

  return (
    <div className={`layout ${isSidebarCollapsed ? "sidebar-collapsed" : ""} entity-${activeEntity}`}>
      {isSidebarCollapsed && (
        <button
          className="sidebar-reopen-btn"
          type="button"
          onClick={() => setIsSidebarCollapsed(false)}
        >
          Show Menu
        </button>
      )}
      <aside className="sidebar">
        <button
          className="sidebar-toggle-btn"
          type="button"
          onClick={() => setIsSidebarCollapsed((prev) => !prev)}
        >
          {isSidebarCollapsed ? "Show Menu" : "Hide Menu"}
        </button>
        <h2>Rent Tracker</h2>
        {ENTITY_OPTIONS.map((entity) => (
          <button
            key={entity}
            className={activeEntity === entity ? "active" : ""}
            onClick={() => handleEntityChange(entity)}
          >
            {entity}
          </button>
        ))}
        <div className="sidebar-divider" />
        <button
          className={activeSection === "financials" ? "active" : ""}
          onClick={() => {
            setActiveSection("financials");
            cancelEditProperty();
          }}
        >
          Financials
        </button>
        <button
          className={activeSection === "property-details" ? "active" : ""}
          onClick={() => setActiveSection("property-details")}
        >
          Property Details
        </button>
        <button
          className={activeSection === "documents" ? "active" : ""}
          onClick={() => setActiveSection("documents")}
        >
          View Documents
        </button>
        <button
          className={activeSection === "tenant-details" ? "active" : ""}
          onClick={() => setActiveSection("tenant-details")}
        >
          Tenant Details
        </button>
        <button
          className={activeSection === "pass-sheet" ? "active" : ""}
          onClick={() => setActiveSection("pass-sheet")}
        >
          Pass Sheet Scanner
        </button>
        <button className="logout-btn" onClick={handleLogout}>
          Logout
        </button>
      </aside>

      <main className="content">
        {generalMessage && <p className="info">{generalMessage}</p>}
        <h1>{activeEntity}</h1>
        {activeSection === "financials" && (
          <>
        <div className="cards">
          <div className="card stat-card">
            <p>Total Receivable (FY)</p>
            <h3>{formatInr(entityTotals.totalReceivable)}</h3>
          </div>
          <div className="card stat-card">
            <p>Total Received</p>
            <h3>{formatInr(entityTotals.totalReceived)}</h3>
          </div>
          <div className="card stat-card">
            <p>Total Balance</p>
            <h3>{formatInr(entityTotals.totalBalance)}</h3>
          </div>
        </div>

        <div className="card action-row">
          <button onClick={() => setActiveForm(activeForm === "add-tenant" ? "" : "add-tenant")}>
            Add Tenant
          </button>
          <button onClick={() => setActiveForm(activeForm === "upload-docs" ? "" : "upload-docs")}>
            Upload Documents
          </button>
        </div>

        {activeForm === "add-tenant" && (
          <form className="card form-grid" onSubmit={handleTenantSubmit} noValidate>
            <h3>{editingTenantId ? "Edit Tenant" : "Add Tenant"}</h3>
            {editingTenantId ? (
              <>
                <p className="edit-tenant-summary">
                  <strong>{formData.tenant_name}</strong> — {formData.property_name}
                </p>
                <p className="rent-mode-hint">Enter either Exempted or Taxable — the rest is calculated automatically.</p>
                <input
                  name="exempted_amount"
                  type="text"
                  inputMode="decimal"
                  placeholder="Exempted (e.g. 50000 or 50,000)"
                  value={formData.exempted_amount}
                  onChange={handleInputChange}
                />
                <input
                  name="taxable_amount"
                  type="text"
                  inputMode="decimal"
                  placeholder="Taxable (e.g. 1794200 or 17,94,200)"
                  value={formData.taxable_amount}
                  onChange={handleInputChange}
                />
                {renderRentPreviewFields()}
                <div className="computed-field">
                  Cumulative Receivable: {formatRentCell(editingTenant?.receivable, inrFormatter)}
                </div>
                <input
                  name="received"
                  type="text"
                  inputMode="decimal"
                  placeholder="Received (Monthly calculation)"
                  value={formData.received}
                  onChange={handleInputChange}
                />
                <div className="computed-field">
                  Monthly Balance: {inrFormatter.format(editFormBalance)}
                </div>
                <input
                  name="received_fy"
                  type="text"
                  inputMode="decimal"
                  placeholder="Received (Totals / FY)"
                  value={formData.received_fy}
                  onChange={handleInputChange}
                />
                <div className="computed-field">FY Balance: {inrFormatter.format(editFormFyBalance)}</div>
                <input
                  name="received_date"
                  type="text"
                  placeholder="Payment date (MM/DD/YYYY)"
                  value={formData.received_date}
                  onChange={handleInputChange}
                />
                <input
                  name="dues_month"
                  placeholder="Due month (e.g., Mar-2026)"
                  value={formData.dues_month}
                  onChange={handleInputChange}
                />
                <input
                  name="remarks"
                  placeholder="Remarks"
                  value={formData.remarks}
                  onChange={handleInputChange}
                />
              </>
            ) : (
              <>
                <input
                  name="tenant_name"
                  placeholder="Tenant Name"
                  value={formData.tenant_name}
                  onChange={handleInputChange}
                  required
                />
                <select name="property_name" value={formData.property_name} onChange={handleInputChange} required>
                  <option value="">Select Property</option>
                  {entityProperties.map((item) => (
                    <option key={item.id} value={item.property_name}>
                      {item.property_name}
                    </option>
                  ))}
                </select>
                <input name="entity_name" value={activeEntity} disabled />
                <input
                  name="advance_paid"
                  type="text"
                  inputMode="decimal"
                  placeholder="Deposit"
                  value={formData.advance_paid}
                  onChange={handleInputChange}
                />
                <input
                  name="advance_paid_date"
                  type="text"
                  placeholder="Deposit Dates (MM/DD/YYYY, MM/DD/YYYY)"
                  value={formData.advance_paid_date}
                  onChange={handleInputChange}
                />
                <p className="rent-mode-hint full-width-label">Monthly rent — enter either Exempted or Taxable (not both).</p>
                <input
                  name="exempted_amount"
                  type="text"
                  inputMode="decimal"
                  placeholder="Exempted (e.g. 50000 or 50,000)"
                  value={formData.exempted_amount}
                  onChange={handleInputChange}
                />
                <input
                  name="taxable_amount"
                  type="text"
                  inputMode="decimal"
                  placeholder="Taxable (e.g. 1794200 or 17,94,200)"
                  value={formData.taxable_amount}
                  onChange={handleInputChange}
                />
                {renderRentPreviewFields()}
                <input
                  name="received"
                  type="text"
                  inputMode="decimal"
                  placeholder="Received — Monthly (optional)"
                  value={formData.received}
                  onChange={handleInputChange}
                />
                <input
                  name="received_fy"
                  type="text"
                  inputMode="decimal"
                  placeholder="Received — Totals / FY (optional)"
                  value={formData.received_fy}
                  onChange={handleInputChange}
                />
                <input
                  name="received_date"
                  type="text"
                  placeholder="Payment date (MM/DD/YYYY)"
                  value={formData.received_date}
                  onChange={handleInputChange}
                />
                <input
                  name="dues_month"
                  placeholder="Due month (e.g., Mar-2026)"
                  value={formData.dues_month}
                  onChange={handleInputChange}
                />
                <input
                  name="remarks"
                  placeholder="Remarks"
                  value={formData.remarks}
                  onChange={handleInputChange}
                />
              </>
            )}
            <div className="form-actions">
              <button type="submit">{editingTenantId ? "Update" : "Add"}</button>
              {editingTenantId && (
                <button type="button" className="secondary" onClick={resetTenantForm}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        )}

        {activeForm === "upload-docs" && (
          <form className="card form-grid" onSubmit={handleUpload}>
            <h3>Upload Documents</h3>
            <select
              name="property_name"
              value={uploadInfo.property_name}
              onChange={handleUploadInput}
              required
            >
              <option value="">Select Property</option>
              {entityProperties.map((item) => (
                <option key={`upload-${item.id}`} value={item.property_name}>
                  {item.property_name}
                </option>
              ))}
            </select>
            <select name="tenant_id" value={uploadInfo.tenant_id} onChange={handleUploadInput}>
              <option value="">Select tenant (optional)</option>
              {tenants
                .filter((item) =>
                  uploadInfo.property_name ? item.property_name === uploadInfo.property_name : true
                )
                .map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.tenant_name}
                  </option>
                ))}
            </select>
            <input
              name="document_name"
              placeholder="Document Name"
              value={uploadInfo.document_name}
              onChange={handleUploadInput}
              required
            />
            <input
              id="doc-file"
              name="file"
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx"
              onChange={handleUploadInput}
              required
            />
            <button type="submit">Upload</button>
          </form>
        )}
        </>
        )}

        {activeSection === "documents" && (
          <div className="card">
            <h3>Documents by Property</h3>
            {Object.keys(documentsByProperty).length === 0 && <p>No documents uploaded yet.</p>}
            {Object.entries(documentsByProperty).map(([propertyName, propertyDocs]) => (
              <div key={propertyName} className="doc-property-block">
                <h4>{propertyName}</h4>
                <table className="rental-table">
                  <thead>
                    <tr>
                      <th>Document Name</th>
                      <th>File Name</th>
                      <th>Uploaded At</th>
                      <th>Download</th>
                      <th>Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {propertyDocs.map((doc) => (
                      <tr key={doc.id}>
                        <td>{doc.document_name || "-"}</td>
                        <td>{doc.file_name}</td>
                        <td>{formatDisplayDate(doc.uploaded_at)}</td>
                        <td>
                          <button className="small-btn" onClick={() => handleDownloadDocument(doc)}>
                            Download
                          </button>
                        </td>
                        <td>
                          <button className="small-btn danger" onClick={() => handleDeleteDocument(doc.id)}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}

        {activeSection === "property-details" && (
          <div className="card property-details-section">
            <h3>Property Details — {activeEntity}</h3>
            <form className="property-row add-property-inline" onSubmit={handleAddProperty}>
              <input
                placeholder={`New property name for ${activeEntity}`}
                value={newPropertyName}
                onChange={(e) => setNewPropertyName(e.target.value)}
              />
              <button type="submit">Add Property</button>
            </form>

            {entityProperties.length === 0 && <p>No properties added yet. Use the form above to add one.</p>}

            <div className="property-details-list">
              {entityProperties.map((property) => {
                const isEditing = String(propertyDetailPropertyId) === String(property.id);
                return (
                  <div className="property-detail-card" key={`property-detail-${property.id}`}>
                    <div className="property-detail-card-header">
                      <h4>{property.property_name}</h4>
                      {!isEditing && (
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <button
                            type="button"
                            className="small-btn"
                            onClick={() => startEditProperty(property)}
                          >
                            Edit Details
                          </button>
                          <button
                            type="button"
                            className="small-btn danger"
                            onClick={() => handleDeleteProperty(property.id)}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>

                    {isEditing ? (
                      <form
                        className="form-grid property-details-form"
                        onSubmit={(event) => {
                          event.preventDefault();
                          savePropertyDetails();
                        }}
                      >
                        <input
                          name="property_name"
                          placeholder="Property Name"
                          value={propertyDetailForm.property_name}
                          onChange={handlePropertyDetailChange}
                          required
                        />
                        <input
                          name="physical_address"
                          placeholder="Physical Address"
                          value={propertyDetailForm.physical_address}
                          onChange={handlePropertyDetailChange}
                        />
                        <input
                          name="google_map_location"
                          type="url"
                          placeholder="Google Maps link (https://maps.google.com/...)"
                          value={propertyDetailForm.google_map_location}
                          onChange={handlePropertyDetailChange}
                        />
                        <input
                          name="building_type"
                          placeholder="Building Type"
                          value={propertyDetailForm.building_type}
                          onChange={handlePropertyDetailChange}
                        />
                        <input
                          name="sq_ft"
                          placeholder="Sq Ft (if any)"
                          value={propertyDetailForm.sq_ft}
                          onChange={handlePropertyDetailChange}
                        />
                        <input
                          name="other_comments"
                          placeholder="Other Comments"
                          value={propertyDetailForm.other_comments}
                          onChange={handlePropertyDetailChange}
                        />
                        <div className="form-actions">
                          <button type="submit">Save</button>
                          <button type="button" className="secondary" onClick={cancelEditProperty}>
                            Cancel
                          </button>
                        </div>
                      </form>
                    ) : (
                      <dl className="property-detail-fields">
                        <div>
                          <dt>Physical Address</dt>
                          <dd>{property.physical_address || "-"}</dd>
                        </div>
                        <div>
                          <dt>Google Maps</dt>
                          <dd>
                            <GoogleMapLink value={property.google_map_location} />
                          </dd>
                        </div>
                        <div>
                          <dt>Building Type</dt>
                          <dd>{property.building_type || "-"}</dd>
                        </div>
                        <div>
                          <dt>Sq Ft</dt>
                          <dd>{property.sq_ft || "-"}</dd>
                        </div>
                        <div>
                          <dt>Other Comments</dt>
                          <dd>{property.other_comments || "-"}</dd>
                        </div>
                      </dl>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeSection === "pass-sheet" && (
          <div className="card pass-sheet-section">
            <h3>Pass Sheet Scanner — {activeEntity}</h3>
            <p className="pass-sheet-help">
              Upload your bank pass sheet Excel file. Matching uses narration rules per entity.
              On approval, both Monthly calculation and Totals received are updated. Deposits
              within ₹10–99 of monthly receivable are rounded to the receivable amount; larger
              differences are reflected in balance.
            </p>

            <form className="form-grid pass-sheet-settings" onSubmit={saveOpenAiKey}>
              <label className="full-width-label">
                OpenAI API Key {openAiConfigured ? "(configured)" : "(optional — improves matching)"}
              </label>
              <input
                type="password"
                placeholder="sk-..."
                value={openAiKeyInput}
                onChange={(e) => setOpenAiKeyInput(e.target.value)}
                autoComplete="off"
              />
              <button type="submit" className="secondary">
                Save API Key
              </button>
            </form>

            <form className="pass-sheet-upload" onSubmit={handlePassSheetScan}>
              <input
                id="pass-sheet-file"
                type="file"
                accept=".xls,.xlsx"
                onChange={handlePassSheetFile}
              />
              <button type="submit" disabled={passSheetScanning || !passSheetFile}>
                {passSheetScanning ? "Scanning…" : "Scan Pass Sheet"}
              </button>
            </form>

            {passSheetResult && (
              <div className="pass-sheet-results">
                <p>
                  <strong>{passSheetResult.rent_payments_found}</strong> of{" "}
                  <strong>{passSheetResult.expected_rent_count}</strong> expected rent payment(s) found.
                </p>

                {passSheetResult.show_missing_rent_alert && passSheetResult.missing_tenants?.length > 0 && (
                  <div className="pass-sheet-alert">
                    <h4>Rent not received — {passSheetResult.missing_tenants.length} property(ies)</h4>
                    <ul>
                      {passSheetResult.missing_tenants.map((item) => (
                        <li key={item.rule_label}>
                          <strong>{item.property_name || item.rule_label}</strong>
                          {item.tenant_name && item.tenant_name !== item.rule_label
                            ? ` — ${item.tenant_name}`
                            : ""}
                        </li>
                      ))}
                    </ul>
                    <p className="pass-sheet-alert-note">
                      These will be marked with due month one month behind and remark &quot;rent not
                      received&quot; when you approve.
                    </p>
                  </div>
                )}

                {passSheetResult.matches?.length > 0 && (
                  <>
                    <h4>Rent received — review and approve</h4>
                    <table className="pass-sheet-approve-table">
                      <thead>
                        <tr>
                          <th>Approve</th>
                          <th>Tenant</th>
                          <th>Date</th>
                          <th>Scanned</th>
                          <th>Credited</th>
                          <th>Due Month(s)</th>
                        </tr>
                      </thead>
                      <tbody>
                      {passSheetResult.matches.map((row) => (
                          <tr
                          key={row.match_id}
                            className={!row.tenant_found ? "tenant-missing" : ""}
                          >
                            <td>
                              <input
                                type="checkbox"
                                checked={Boolean(passSheetApprovals[row.match_id])}
                                disabled={!row.tenant_id}
                                onChange={() => togglePassSheetApproval(row.match_id)}
                              />
                            </td>
                            <td>
                              {row.tenant_name}
                              {!row.tenant_found && (
                                <span className="warning-tag"> Not in DB</span>
                              )}
                              {row.rounded_to_receivable && (
                                <span className="rounding-tag"> Rounded</span>
                              )}
                            </td>
                            <td className="pass-sheet-date-cell">{row.date || "-"}</td>
                            <td>{formatInr(row.deposit_amount)}</td>
                            <td>{formatInr(row.credited_amount ?? row.deposit_amount)}</td>
                            <td>
                              <div className="pass-sheet-month-checkboxes">
                                {getDueMonthPickerOptions(row.proposed_dues_month).map((month) => {
                                  const selectedMonths =
                                    passSheetDueMonths[row.match_id] ??
                                    parseDueMonthSelection(row.proposed_dues_month);
                                  return (
                                    <label key={`${row.match_id}-${month}`} className="pass-sheet-month-option">
                                      <input
                                        type="checkbox"
                                        checked={selectedMonths.includes(month)}
                                        disabled={!row.tenant_id}
                                        onChange={(event) =>
                                          togglePassSheetDueMonth(row.match_id, month, event.target.checked)
                                        }
                                      />
                                      <span>{month}</span>
                                    </label>
                                  );
                                })}
                              </div>
                              <div className="pass-sheet-month-summary">
                                {formatDueMonthSelection(
                                  passSheetDueMonths[row.match_id] ??
                                    parseDueMonthSelection(row.proposed_dues_month)
                                ) || "Select month(s)"}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <button type="button" className="apply-pass-sheet-btn" onClick={handlePassSheetApply}>
                      Approve &amp; Apply to Financials
                    </button>
                  </>
                )}

              </div>
            )}

            {activeEntity === "VTPL" && (
              <div className="pass-sheet-rules-hint">
                <h4>VTPL matching rules</h4>
                <ul>
                  <li>SJS — SJS ENTERPRISES</li>
                  <li>REACH NVP — Consultancy fee (REACH NVY / REACH NVP)</li>
                  <li>PRANAVA 1st FLR — LOHITH</li>
                  <li>PRANAVA 2nd FLR — MAHALAKSHMI</li>
                  <li>PRANAVA 3rd FLR — GANAPATHI RAMACHANDR</li>
                  <li>PRANAVA Terrance — KSHITIJ</li>
                  <li>Mysore Warehouse — ASTRAL COATINGS (ASTRAL COATINGS / ASTRAL in narration)</li>
                </ul>
              </div>
            )}

            {activeEntity === "HUF" && (
              <div className="pass-sheet-rules-hint">
                <h4>HUF matching rules (HSR Building)</h4>
                <ul>
                  <li>G FLR — Dr Sajeev Core Health Care (DR SAJEEV CORE / SANJEEV CORE HEALTHCARE)</li>
                  <li>G FLR — Safehands (SAFEHANDS)</li>
                  <li>1st FLR — Pure Play Media (PURE PLAY)</li>
                  <li>2nd FLR — Hitesh Bhagwan Devnani (ZHEN SPACE)</li>
                  <li>3rd FLR — VH Realtors (VH REALTORS)</li>
                </ul>
              </div>
            )}

            {activeEntity === "RNS" && (
              <div className="pass-sheet-rules-hint">
                <h4>RNS matching rules</h4>
                <ul>
                  <li>Lenskart — Chandra Layout Building (DEALSKART)</li>
                  <li>Candor — Chandra Layout Building (CANDOR)</li>
                  <li>Ferro Precision Engineering — Hosakote (FERRO PRECISION / FERRO)</li>
                  <li>Study Center — Chandra Layout Building (NR ASSOCIATES)</li>
                  <li>Excise License — SAMS Spirits (LIQUOR / SAM DROPS)</li>
                </ul>
              </div>
            )}

            {activeEntity === "TRS" && (
              <div className="pass-sheet-rules-hint">
                <h4>TRS matching rules</h4>
                <p style={{ margin: 0 }}>
                  TRS rules are not configured yet. Add TRS properties/tenants and their narration rules
                  in the backend when ready.
                </p>
              </div>
            )}
          </div>
        )}

        {activeSection === "tenant-details" && (
          <div className="card">
            <h3>Tenant Details by Property</h3>
            <form className="form-grid" onSubmit={saveTenantDetails}>
              <select
                name="tenant_id"
                value={tenantDetailForm.tenant_id}
                onChange={handleTenantDetailSelection}
                required
              >
                <option value="">Select Tenant</option>
                {tenants.map((tenant) => (
                  <option key={`tenant-detail-select-${tenant.id}`} value={tenant.id}>
                    {tenant.tenant_name} - {tenant.property_name}
                  </option>
                ))}
              </select>
              <input
                name="tenant_email"
                type="email"
                placeholder="Tenant Email"
                value={tenantDetailForm.tenant_email}
                onChange={handleTenantDetailInput}
              />
              <input
                name="tenant_contact_no"
                placeholder="Tenant Contact No"
                value={tenantDetailForm.tenant_contact_no}
                onChange={handleTenantDetailInput}
              />
              <input
                name="tenant_address"
                placeholder="Tenant Address"
                value={tenantDetailForm.tenant_address}
                onChange={handleTenantDetailInput}
              />
              <button type="submit">Save Tenant Details</button>
            </form>
            {groupedPropertyData.length === 0 && <p>No properties added yet.</p>}
            {groupedPropertyData.map((property) => (
              <div key={`tenant-detail-${property.id}`} className="doc-property-block">
                <h4>{property.property_name}</h4>
                <table className="rental-table">
                  <thead>
                    <tr>
                      <th>Tenant</th>
                      <th>Email</th>
                      <th>Contact No</th>
                      <th>Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {property.tenants.length === 0 && (
                      <tr>
                        <td colSpan="4">No tenants for this property.</td>
                      </tr>
                    )}
                    {property.tenants.map((tenant) => (
                      <tr key={`tenant-contact-${tenant.id}`}>
                        <td>{tenant.tenant_name}</td>
                        <td>{tenant.tenant_email || "-"}</td>
                        <td>{tenant.tenant_contact_no || "-"}</td>
                        <td>{tenant.tenant_address || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}

        {activeSection === "financials" && (
        <div className="financials-split">
        <div className="card financials-panel">
          <h3>Monthly calculation</h3>
          {groupedPropertyData.length === 0 && <p>No properties added yet.</p>}
          <div className="property-list-wrap">
            {groupedPropertyData.map((property) => (
              <div className="property-block" key={`prop-block-${property.id}`}>
                <div className="property-header">
                  <h4 className="property-title-static">{property.property_name}</h4>
                </div>

                <div className="table-card financial-table-wrap">
                  <table className="rental-table financial-table">
                    <thead>
                      <tr>
                        <th>Tenant</th>
                        <th>Exempted</th>
                        <th>Taxable</th>
                        <th>GST</th>
                        <th>Total Rent</th>
                        <th>TDS</th>
                        <th>Receivable</th>
                        <th>Received</th>
                        <th>Date</th>
                        <th>Balance</th>
                        <th>Due Month</th>
                        <th>Remarks</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {property.tenants.length === 0 && (
                        <tr>
                          <td colSpan="13">No tenants for this property.</td>
                        </tr>
                      )}
                      {property.tenants.map((tenant) => (
                        <React.Fragment key={tenant.id}>
                          <tr>
                            <td>
                              <button
                                className="tenant-link"
                                onClick={() =>
                                  setSelectedTenantId((prev) => (prev === tenant.id ? null : tenant.id))
                                }
                              >
                                {tenant.tenant_name}
                              </button>
                            </td>
                            <td>{formatRentCell(tenant.exempted_amount, inrFormatter)}</td>
                            <td>{formatRentCell(tenant.taxable_amount, inrFormatter)}</td>
                            <td>{formatRentCell(tenant.gst, inrFormatter)}</td>
                            <td>{formatRentCell(tenant.total_rent, inrFormatter)}</td>
                            <td>{formatRentCell(tenant.tds, inrFormatter)}</td>
                            <td>{formatInr(tenant.receivable)}</td>
                            <td>{formatInr(tenant.received)}</td>
                            <td>{formatDisplayDate(tenant.received_date)}</td>
                            <td>{formatInr(tenant.balance_receivable)}</td>
                            <td className={getDueClass(tenant.dues_month)}>{tenant.dues_month || "-"}</td>
                            <td className="remarks-cell">{tenant.remarks || "-"}</td>
                            <td>
                              <button className="small-btn" onClick={() => startEditTenant(tenant)}>
                                Edit
                              </button>
                              <button className="small-btn danger" onClick={() => handleDeleteTenant(tenant.id)}>
                                Delete
                              </button>
                            </td>
                          </tr>
                          {selectedTenantId === tenant.id && (
                            <tr>
                              <td colSpan="13" className="tenant-detail">
                                <strong>Deposit:</strong> {formatInr(tenant.advance_paid)} |{" "}
                                <strong>Date(s):</strong> {formatDisplayDateList(tenant.advance_paid_date)} |{" "}
                                <strong>Monthly rent:</strong> {formatInr(tenant.monthly_receivable)}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card financials-panel">
          <h3>Totals</h3>
          <p className="fy-range-label">
            Financial year: {fyRangeLabel} ({fyMonthCount} month{fyMonthCount === 1 ? "" : "s"})
          </p>
          {fyTotalsByProperty.length === 0 && <p>No properties added yet.</p>}
          <div className="property-list-wrap">
            {fyTotalsByProperty.map((property) => (
              <div className="property-block" key={`totals-${property.propertyId}`}>
                <div className="property-header">
                  <h4 className="property-title-static">{property.propertyName}</h4>
                </div>
                <div className="table-card totals-table-wrap">
                  <table className="rental-table totals-table">
                    <thead>
                      <tr>
                        <th>Tenant</th>
                        <th>Receivable</th>
                        <th>Received</th>
                        <th>Balance</th>
                        <th>Due Month</th>
                      </tr>
                    </thead>
                    <tbody>
                      {property.tenants.length === 0 && (
                        <tr>
                          <td colSpan="5">No tenants for this property.</td>
                        </tr>
                      )}
                      {property.tenants.map((row) => (
                        <tr key={`fy-total-${row.tenantId}`}>
                          <td>{row.tenantName}</td>
                          <td>{formatInr(row.fyReceivable)}</td>
                          <td>{formatInr(row.received)}</td>
                          <td>{formatInr(row.balance)}</td>
                          <td className={getDueClass(row.duesMonth)}>{row.duesMonth}</td>
                        </tr>
                      ))}
                      {property.tenants.length > 0 && (
                        <tr className="property-total-row">
                          <td><strong>Property Total</strong></td>
                          <td><strong>{formatInr(property.totals.fyReceivable)}</strong></td>
                          <td><strong>{formatInr(property.totals.received)}</strong></td>
                          <td><strong>{formatInr(property.totals.balance)}</strong></td>
                          <td />
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
          {fyTotalsByProperty.some((p) => p.tenants.length > 0) && (
            <div className="entity-grand-total">
              <span><strong>Entity Total</strong></span>
              <span><strong>{formatInr(entityTotals.totalReceivable)}</strong></span>
              <span><strong>{formatInr(entityTotals.totalReceived)}</strong></span>
              <span><strong>{formatInr(entityTotals.totalBalance)}</strong></span>
            </div>
          )}
        </div>
        </div>
        )}
      </main>
    </div>
  );
}

export default App;
